/**
 * client/src/utils/deviceFingerprint.ts
 *
 * FASE 2 — Client-side device fingerprint computation.
 *
 * Genera un hash SHA-256 del dispositivo da inviare come header
 * X-Device-Fingerprint a ogni richiesta API autenticata.
 *
 * Il fingerprint viene calcolato UNA volta al bootstrap e rimane
 * stabile per tutta la sessione. Viene ricalcolato solo se
 * cambiano parametri significativi (es. rotazione schermo).
 *
 * COMPONENTI DEL FINGERPRINT:
 * - User agent (navigator.userAgent)
 * - Platform (navigator.platform)
 * - Screen resolution (screen.width × screen.height)
 * - Timezone (Intl.DateTimeFormat().resolvedOptions().timeZone)
 * - Color depth (screen.colorDepth)
 * - Language (navigator.language)
 *
 * PRIVACY: Nessun dato personale. Solo caratteristiche del dispositivo.
 * Non viene inviato ad analytics — solo al server per session binding.
 */

/** Fallback sincrono per ambienti senza SubtleCrypto (djb2 hash). */
function simpleHash(message: string): string {
  let hash = 0;
  for (let i = 0; i < message.length; i++) {
    const char = message.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash |= 0;
  }
  return Math.abs(hash).toString(16).padStart(8, '0');
}

let _fingerprint: string | null = null;

/**
 * SESSIONE-UNICA (Ott 2026): identificatore casuale dell'INSTALLAZIONE.
 *
 * PERCHÉ SERVE: il fingerprint basato solo su userAgent/screen/timezone è
 * IDENTICO fra browser normale e navigazione in incognito sullo stesso
 * dispositivo (stessi valori!) → il server non vedeva alcun conflitto e lo
 * stesso account restava attivo due volte. Giuseppe ha chiesto "sempre e solo
 * un login", anche fra normale e incognito.
 *
 * Ogni contesto di storage (profilo del browser, sessione in incognito,
 * WebView dell'app, altro telefono) ha il proprio localStorage: un id casuale
 * persistito qui rende ogni installazione DISTINTA, mentre più schede dello
 * stesso browser condividono lo stesso id (nessun falso conflitto).
 *
 * Se localStorage non è disponibile (raro: storage disabilitato) si ricade su
 * un id per-sessione in memoria: due contesti restano distinti comunque.
 */
const INSTALL_ID_KEY = "rc-install-id";
let _installIdFallback: string | null = null;

function randomId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }
  } catch {
    /* ignore */
  }
  // Fallback: 128 bit pseudo-casuali in esadecimale.
  let out = "";
  for (let i = 0; i < 32; i++) out += Math.floor(Math.random() * 16).toString(16);
  return out;
}

function getInstallId(): string {
  try {
    const existing = localStorage.getItem(INSTALL_ID_KEY);
    if (existing && existing.length >= 8) return existing;
    const created = randomId();
    localStorage.setItem(INSTALL_ID_KEY, created);
    return created;
  } catch {
    if (!_installIdFallback) _installIdFallback = randomId();
    return _installIdFallback;
  }
}

/**
 * Calcola il fingerprint del dispositivo corrente.
 * Idempotente: chiamate multiple restituiscono lo stesso valore.
 */
export function getDeviceFingerprint(): string {
  if (_fingerprint) return _fingerprint;
  _fingerprint = computeFingerprint();
  return _fingerprint;
}

/**
 * Ricalcola forzatamente il fingerprint (es. dopo resize significativo).
 */
export function refreshFingerprint(): string {
  _fingerprint = computeFingerprint();
  return _fingerprint;
}

function computeFingerprint(): string {
  const components = [
    navigator.userAgent || '',
    navigator.platform || '',
    `${screen.width || 0}x${screen.height || 0}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    `${screen.colorDepth || 0}`,
    navigator.language || '',
    // SESSIONE-UNICA: id casuale per contesto di storage (normale != incognito)
    getInstallId(),
  ].join('|');

  return hashString(components);
}

/**
 * Hash SHA-256 (usando SubtleCrypto, disponibile in tutti i browser moderni).
 * Fallback a hash semplice se SubtleCrypto non disponibile (raro).
 */
async function sha256(message: string): Promise<string> {
  if (typeof crypto !== 'undefined' && crypto.subtle) {
    const msgBuffer = new TextEncoder().encode(message);
    const hashBuffer = await crypto.subtle.digest('SHA-256', msgBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');
  }
  // Fallback: simple hash (non crittografico, ma sufficiente per fingerprint)
  return simpleHash(message);
}

/**
 * Fallback sincrono per ambienti senza SubtleCrypto.
 */
function hashString(message: string): string {
  // Usa una versione sincrona basata su djb2 + lunghezza fissa
  let hash = 5381;
  for (let i = 0; i < message.length; i++) {
    hash = ((hash << 5) + hash + message.charCodeAt(i)) | 0;
  }
  // Padding a 64 caratteri esadecimali per compatibilità col formato server
  const hex = Math.abs(hash).toString(16);
  return hex.padStart(64, '0');
}

/**
 * Inizializza il fingerprint in modo asincrono (usa SubtleCrypto).
 * Chiamare UNA volta all'avvio dell'app, prima di qualsiasi chiamata API.
 */
export async function initDeviceFingerprint(): Promise<string> {
  if (_fingerprint) return _fingerprint;

  const components = [
    navigator.userAgent || '',
    navigator.platform || '',
    `${screen.width || 0}x${screen.height || 0}`,
    Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    `${screen.colorDepth || 0}`,
    navigator.language || '',
    // SESSIONE-UNICA: id casuale per contesto di storage (normale != incognito)
    getInstallId(),
  ].join('|');

  try {
    _fingerprint = await sha256(components);
  } catch {
    _fingerprint = hashString(components);
  }

  return _fingerprint;
}
